import React, { Component } from 'react';
import saveAs from 'file-saver';
import _ from 'lodash';
import QueryView from './QueryView';
import DateFormatter from '../../utils/DateFormatter';
import queryStore from './queryStore';
import { mediaBrowserFetchMediaItem } from '../../store/mediaBrowser/index';
import { categoryFetchQuery } from '../../store/models/Category';

function hitToTimestamp(hit) {
  const label = _.get(hit, 'conditions[0]', 'hit');
  const s = _.get(hit, 'fieldValues.startMs', 0);
  const e = _.get(hit, 'fieldValues.endMs', 0);
  if (!e) {
    return null;
  }
  return { label, s: [s], e: [e] };
  // "[{"score":5.480382919311523,"conditions":["phrase: checkers"],"fieldValues":{"startMs":653160,"endMs":657530,"startRatio":0.97582257,"line":128,"speaker":"Unknown","text":"and I little girl Tricia the 6 year old named it Checkers"}}]"
}

const API_URL = process.env.API_URL; // eslint-disable-line
const PLAYER_URL = process.env.PLAYER_URL;
const BOUND_FUNCTIONS = _([
  'goHome',
  'downloadQuery',
  'doShowMedia',
])
  .uniq()
  .compact()
  .value();

const FIELD_RENDERERS = {
  speakerWordCount(swc) {
    if (!swc) {
      return '';
    }
    let out = '';
    const keys = Object.keys(swc);
    if (keys.length === 1 && keys[0] === 'Unknown') {
      return swc.Unknown;
    }
    keys.forEach((key) => {
      out += `${key}: ${swc[key]},`;
    });
    return out;
  },
  categories(cats) {
    return _(Object.keys(cats))
      .map((category) => {
        if (cats[category]) {
          return category;
        }
        return false;
      })
      .compact()
      .map(term => ((term.length) > 20 ? `${term.substr(0, 10)}...` : term))
      .sortBy(_.identity)
      .value()
      .join(', ');
  },
};

export default class QueryContainer extends Component {
  constructor(props) {
    super(props);
    this.store = queryStore();
    this.state = {
      ...this.store.state,
    };
    BOUND_FUNCTIONS.forEach((fn) => {
      if (this[fn] && _.isFunction(this[fn])) {
        this[fn] = this[fn].bind(this);
      } else {
        console.log('cannot find function ', fn);
      }
    });
    this.store.actions.setToken(_.get(props, 'ephemeralKey.bearerToken'));
  }

  goHome() {
    this.props.history.push('/');
  }

  async downloadQuery() {
    if (typeof Blob === 'undefined') {
      alert('cannot save query. sorry.');
      return;
    }
    const date = new Date();
    const store = this.store;
    // eslint-disable-next-line prefer-const
    let { queryName, queryModel } = store.state;
    if (!queryName) {
      queryName = `query ${Math.random()}`;
    }
    const json = {
      query: queryModel.query,
      composed: queryModel.composed(),
      saved: date.toISOString(),
      name: queryName,
    };

    let file;
    try {
      file = new Blob([JSON.stringify(json)], { type: 'application/json;charset=utf-8' });
    } catch (err) {
      console.log('error converting file', err);
      alert(`cannot save file:${err.message}`);
      return;
    }

    await this.store.actions.setUploading(true);
    saveAs(file, `${queryName} # ${DateFormatter.formatForFile(new Date())}.json`);
    this.store.actions.setUploading(false);
    this.store.actions.setQueryName('');
  }

  componentWillUnmount() {
    this.unmounted = true;
    if (this._stateSub) {
      this._stateSub.unsubscribe();
    }
  }

  /**
   * sets state if the component has not been unmounted.
   * returns a promise tied into setState (or auto-completes if no set state call)
   * @param change
   * @param onDone
   * @returns {Promise<any>}
   */
  setStateSafely(change, onDone) {
    return new Promise((done) => {
      if (!this.unmounted) {
        this.setState(change, () => {
          if (_.isFunction(onDone)) {
            onDone();
          }
          done();
        });
      } else {
        done();
      }
    });
  }

  async computeUrl(mediaId, externalHits = '') {
    const { token } = this.state;
    console.log('computeUrl: externalHits', externalHits);
    let url = '';
    if (mediaId) {
      const mediaData = await mediaBrowserFetchMediaItem(token, mediaId);
      console.log('media data:', mediaData);

      // "[{"score":5.480382919311523,"conditions":["phrase: checkers"],"fieldValues":{"startMs":653160,"endMs":657530,"startRatio":0.97582257,"line":128,"speaker":"Unknown","text":"and I little girl Tricia the 6 year old named it Checkers"}}]"

      if (externalHits) {
        try {
          const j = JSON.parse(externalHits);
          if (_.isArray(j)) {
            const hits = _(j).map(hitToTimestamp).compact().value();
            // eslint-disable-next-line no-param-reassign
            externalHits = JSON.stringify({
              SectionTitle: 'Query Hits',
              hits,
            });
            console.log('external hits: ', externalHits);
          }
        } catch (err) {
          console.log('error making hits: ', err);
        }
      }
      const playerConfig = {
        searchTimeRanges: '',
        analyticsFormat: 'ANALYTICS_SCHEMA_VERSION_V3',
        mediaId,
        externalHits,
        height: true,
        token,
        apiUrl: API_URL,
      };

      const entries = _(Object.keys(playerConfig))
        .map((k) => {
          const value = encodeURIComponent(playerConfig[k]);
          if (!value) return '';
          return `${encodeURIComponent(k)}=${value}`;
        })
        .compact()
        .join('&');

      console.log('entries', entries);
      url = `${PLAYER_URL}/index.html?index=0#${entries}`;
    }
    return url;
  }

  componentDidMount() {
    this._stateSub = this.store
      .subscribe((store) => {
        this.setState(state => ({ ...state, ...store.state }));
      }, (err) => {
        console.log('error on store: ', err);
        this.store.restart();
      }, () => {
      });
    this.store.actions.loadCategories();
    this.store.actions.loadSchema()
      .then(() => {
        if (this.store.state.schemaFetchError) {
          console.log('----- schema fetch error: ', this.store.state.schemaFetchError);
        }
      });
  }

  async doShowMedia(mid, hits) {
    const url = await this.computeUrl(mid, hits);
    this.store.actions.showMedia(mid, url);
  }

  render() {
    const { state, actions } = this.store;
    return (
      <QueryView
        goHome={this.goHome}
        downloadQuery={this.downloadQuery}
        doShowMedia={this.doShowMedia}
        {...this.state}
        {...state}
        {...actions}
      />
    );
  }
}
